import React, { useState, useRef, useEffect, RefObject } from "react";
import { ButtonGroup, MenuItem, MenuList, TooltipProps } from "@mui/material";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { icon as faIcon } from "../..";
import styles from "./CustomSplitButtonMenu.module.css";
import { CustomTooltip } from "../../tooltip";
import DropDown from "../../drop-down/DropDown";
import CustomButton from "../custom-button/CustomButton";
import CustomIconButton from "../custom-icon-button/CustomIconButton";

type SvgIconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;
type IconSource = IconProp | SvgIconComponent;

type ButtonSize = "xs" | "sm" | "md" | "lg";
type ButtonColor = "primary" | "secondary";
type ButtonVariant = "text" | "outlined" | "contained";

export interface SplitButtonOption {
	label: string;
	value: string;
	tooltip?: TooltipProps["title"];
	disabled?: boolean;
	subLabel?: string;
}

interface CustomSplitButtonMenuProps {
	color?: ButtonColor;
	disabled?: boolean;
	icon?: IconSource;
	onSelect: (option: SplitButtonOption) => void;
	options: SplitButtonOption[];
	size?: ButtonSize;
	tooltip?: TooltipProps["title"];
	value?: string;
	variant?: ButtonVariant;
}

// options shape : [{label: "", value: "", tooltip: "optional", disabled: false}] // label to display, value to compare
export default function CustomSplitButtonMenu({
	color = "primary",
	disabled = false,
	icon = undefined,
	onSelect,
	options,
	size = "md",
	tooltip = "",
	value,
	variant = "outlined",
}: CustomSplitButtonMenuProps) {
	const anchorRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	const [hovered, setHovered] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(0);
	useEffect(() => {
		if (disabled) {
			setOpen(false);
		}
	}, [disabled]);
	useEffect(() => {
		const getSelectedIndex = (selectedValue: string | undefined): number => {
			if (selectedValue && Array.isArray(options) && options.some((opt) => opt.value === value)) {
				return options.findIndex((opt) => opt.value === value);
			}
			return 0;
		};
		setSelectedIndex(getSelectedIndex(value));
	}, [value, options]);
	const handleChoose = (index: number) => onSelect(options[index]);
	const handleMouseOver = () => setHovered(true);
	const handleMouseLeave = () => setHovered(false);
	const handleMenuItemClick = (ev: React.MouseEvent<HTMLLIElement>) => {
		const index = ev.currentTarget.value;
		setSelectedIndex(index);
		setOpen(false);
		handleChoose(index);
	};

	const handleToggle = () => setOpen((prevOpen) => !prevOpen);

	const handleClose = (event: MouseEvent | TouchEvent, ref: RefObject<HTMLElement>) => {
		if (ref.current && ref.current.contains(event.target as Node)) {
			return;
		}
		setOpen(false);
	};
	return (
		<>
			<CustomTooltip arrow placement="right" title={tooltip}>
				<div>
					<ButtonGroup
						ref={anchorRef}
						disableElevation
						className={styles.splitBtn__btnGroup}
						disabled={disabled}
					>
						<CustomButton
							className={styles.splitBtn__btn}
							color={color}
							size={size}
							startIcon={icon}
							variant={variant}
							onClick={handleToggle}
							onMouseLeave={handleMouseLeave}
							onMouseOver={handleMouseOver}
						>
							{options[selectedIndex].label}
						</CustomButton>
						<CustomIconButton
							btnClassName={`${styles.splitBtn__iconBtn} ${
								hovered || selectedIndex >= -1 ? styles["splitBtn__iconBtn--active"] : ""
							}`}
							color={color}
							icon={open ? faIcon.faCaretUp : faIcon.faCaretDown}
							size={size}
							variant={variant}
							onClick={handleToggle}
							onMouseLeave={handleMouseLeave}
							onMouseOver={handleMouseOver}
						/>
					</ButtonGroup>
				</div>
			</CustomTooltip>
			<DropDown ref={anchorRef} open={open} position="bottom" onClose={handleClose}>
				<MenuList id="split-button-menu">
					{Array.isArray(options) &&
						options.map((option, index) => (
							<CustomTooltip key={option.value} arrow placement="right" title={option.tooltip || ""}>
								<div>
									<MenuItem
										className={styles.splitBtn__option}
										disabled={index === selectedIndex || option.disabled}
										selected={index === selectedIndex}
										value={index}
										onClick={handleMenuItemClick}
									>
										{option.label}
										<div className={styles["splitBtn__option--subLabel"]}>{option.subLabel}</div>
									</MenuItem>
								</div>
							</CustomTooltip>
						))}
				</MenuList>
			</DropDown>
		</>
	);
}
